import { useState } from "react";
import type { Block, FigureData } from "../../../../models/ManualAuthoring";
import { useManualAuthoringStore } from "../../../../store/manualAuthoringStore";
import { useAuthoringStore } from "../../../../store/UseAuthorStore";
import { masterAssemblyApi } from "../../../../apiServices/masterassembly";
import "./figure.css";

export default function FigureEdit({
  block,
  figureNumber,
  onDone,
}: {
  block: Block;
  figureNumber?: number;
  onDone: () => void;
}) {
  const updateBlock = useManualAuthoringStore((s) => s.updateBlock);
  const oemName = useAuthoringStore((s) => s.oemName);
  const masterAssembly = useAuthoringStore((s) => s.masterAssembly);
  const data = block.data as FigureData;
  const [pendingFile, setPendingFile] = useState<File | null>(null);
  const [isUploading, setIsUploading] = useState(false);
  const [uploadError, setUploadError] = useState<string | null>(null);

  const baseUrl = process.env.REACT_APP_API_BASE_URL || "";
  const resolveSrc = (folderPath: string) => {
    if (!folderPath) return "";
    if (/^https?:\/\//i.test(folderPath)) return folderPath;
    if (folderPath.startsWith("/media/")) return `${baseUrl}${folderPath}`;
    if (folderPath.startsWith("media/")) return `${baseUrl}/${folderPath}`;
    return `${baseUrl}/media/${folderPath}`;
  };
  const normalizeFolderPath = (path: string) => {
    if (!path) return "";
    let normalized = path;
    if (baseUrl && normalized.startsWith(baseUrl)) {
      normalized = normalized.slice(baseUrl.length);
    }
    if (normalized.startsWith("/media/")) normalized = normalized.slice("/media/".length);
    if (normalized.startsWith("media/")) normalized = normalized.slice("media/".length);
    if (normalized.startsWith("/")) normalized = normalized.slice(1);
    return normalized;
  };

  const handleUpload = async (file?: File) => {
    const targetFile = file ?? pendingFile;
    if (!targetFile) return;
    const masterAssemblyId = masterAssembly?.masterAssemblyId;
    if (!masterAssemblyId || masterAssemblyId <= 0) {
      setUploadError("Missing master assembly id.");
      return;
    }
    setIsUploading(true);
    setUploadError(null);
    try {
      const meta = await masterAssemblyApi.uploadSvg({
        file: targetFile,
        masterAssemblyId,
        figNumber: block.id,
        oem: oemName || "Oem1",
      });

      if (meta?.folderPath) {
        const folderPath = normalizeFolderPath(meta.folderPath);
        updateBlock(block.id, { svgId: meta.svgId || 0, folderPath });
      }
      setPendingFile(null);
    } catch {
      setUploadError("Upload failed.");
    } finally {
      setIsUploading(false);
    }
  };

  const updateCaption = (value: string) => {
    updateBlock(block.id, { caption: value });
  };

  return (
    <div className="pdf-block pdf-figure pdf-figure--editing">
      {data.folderPath && (
        <img
          src={resolveSrc(data.folderPath)}
          alt="Figure"
          className="pdf-figure__image"
        />
      )}

      <div className="pdf-figure__controls">
        <input
          type="file"
          accept="image/*"
          className="pdf-figure__file-input"
          onChange={(e) => {
            if (e.target.files?.[0]) {
              const file = e.target.files[0];
              setPendingFile(file);
              handleUpload(file);
            }
          }}
        />
        <button
          type="button"
          className="btn btn-sm btn-primary mt-2"
          disabled={!pendingFile || isUploading}
          onClick={() => handleUpload()}
        >
          {isUploading ? "Uploading..." : "Upload"}
        </button>
        {uploadError && (
          <div className="text-danger small mt-1">{uploadError}</div>
        )}
      </div>

      <div className="pdf-figure__caption-edit-row">
        <span className="pdf-figure__caption-label">
          Figure {figureNumber ?? "—"}.
        </span>
        <input
          className="pdf-figure__caption-edit"
          value={data.caption ?? ""}
          onChange={(e) => updateCaption(e.target.value)}
          onBlur={onDone}
          placeholder="Enter figure caption"
        />
      </div>
    </div>
  );
}
